import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pencil, Plus, Trash2, Clock, Mail, MessageSquare, Bell, Copy, Lock } from "lucide-react";
import { useNotificationTemplates, NotificationTemplate, STANDALONE_TEMPLATE_OWNERS } from "@/hooks/useNotificationTemplates";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface NotificationTemplatesListProps {
  onEdit: (template: NotificationTemplate) => void;
  onCreateNew: () => void;
  onDuplicate: (template: NotificationTemplate) => void;
}

export function NotificationTemplatesList({ onEdit, onCreateNew, onDuplicate }: NotificationTemplatesListProps) {
  const { templates, isLoading, toggleActive, deleteTemplate, isDeleting } = useNotificationTemplates();

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  const formatHoursBefore = (hours: number) => {
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return remainingHours > 0 
        ? `${days}d ${remainingHours}h before`
        : `${days} day${days > 1 ? 's' : ''} before`;
    }
    return `${hours}h before`;
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case "email": return Mail;
      case "whatsapp": return MessageSquare;
      case "in_app": return Bell;
      default: return Mail;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Notification Templates</h3>
        <Button onClick={onCreateNew} size="sm" className="gap-2">
          <Plus className="h-4 w-4" />
          New Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No templates configured. Create one to get started.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Timing</TableHead>
              <TableHead>Channels</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.map((template) => {
              const isStandalone = template.template_type === "standalone";
              const owner = STANDALONE_TEMPLATE_OWNERS[template.name];
              return (
              <TableRow key={template.id}>
                <TableCell className="font-medium">
                  <div className="flex flex-col gap-1">
                    <span>{template.name}</span>
                    {isStandalone && (
                      <Badge variant="outline" className="w-fit gap-1 font-normal">
                        <Lock className="h-3 w-3" />
                        Managed by {owner ?? "its own function"}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {isStandalone ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      {formatHoursBefore(template.hours_before)}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {template.channels.map((channel) => {
                      const Icon = getChannelIcon(channel);
                      return (
                        <Badge key={channel} variant="secondary" className="gap-1">
                          <Icon className="h-3 w-3" />
                          {channel === "in_app" ? "In-App" : channel.charAt(0).toUpperCase() + channel.slice(1)}
                        </Badge>
                      );
                    })}
                  </div>
                </TableCell>
                <TableCell>
                  <Switch
                    checked={template.is_active}
                    onCheckedChange={(checked) => toggleActive({ id: template.id, is_active: checked })}
                  />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDuplicate(template)}
                      title="Duplicate template"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(template)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {!isStandalone && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Template</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{template.name}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteTemplate(template.id)}
                              disabled={isDeleting}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
